import {
  PlusOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Button, Modal, Table, message, Pagination, Tag } from "antd";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import { API_URL, TOKEN } from "../../../variables/constants";
import AddTypesPieces from "./AddTypesPieces";
import ActionDropdown from "../../production/components/ActionDropdown";

const { confirm } = Modal;

const TypePieces = () => {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedTypePiece, setSelectedTypePiece] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);

  // pagination locale
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handleShow = (edit = false, item = null) => {
    setEditMode(edit);
    setSelectedTypePiece(item);
    setShowModal(true);
  };
  const handleClose = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedTypePiece(null);
  };

  const fetchTypePieces = async () => {
    setLoadingTable(true);
    try {
      const res = await axios.get(`${API_URL}/api/type-piece`, {
        headers: { Authorization: `Bearer ${TOKEN}` },
      });
      const sorted = [...res.data].sort((a, b) => {
        const ida = a.typePieceId ?? 0;
        const idb = b.typePieceId ?? 0;
        return idb - ida;
      });
      setItems(sorted);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la récupération des types de pièces.");
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    fetchTypePieces();
  }, []);

  const handleAddSuccess = (created) => {
    setItems((prev) => [created, ...prev]);
    setCurrentPage(1);
  };

  const handleUpdateSuccess = (updated) => {
    setItems((prev) => {
      const withoutOld = prev.filter(
        (p) => p.typePieceId !== updated.typePieceId
      );
      return [updated, ...withoutOld];
    });
  };

  const handleDelete = (typePieceId) => {
    confirm({
      title: "Voulez-vous vraiment supprimer ce type de pièce ?",
      icon: <ExclamationCircleOutlined />,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      async onOk() {
        try {
          await axios.delete(`${API_URL}/api/type-piece/${typePieceId}`, {
            headers: { Authorization: `Bearer ${TOKEN}` },
          });
          setItems((prev) => prev.filter((p) => p.typePieceId !== typePieceId));
          message.success("Type de pièce supprimé avec succès.");

          const totalAfter = items.length - 1;
          const totalPagesAfter = Math.ceil(totalAfter / pageSize);
          if (currentPage > totalPagesAfter && totalPagesAfter > 0) {
            setCurrentPage(totalPagesAfter);
          } else if (totalPagesAfter === 0) {
            setCurrentPage(1);
          }
        } catch (err) {
          console.error(err);
          message.error("Erreur lors de la suppression du type de pièce.");
        }
      },
    });
  };

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = useMemo(
    () => items.slice(startIndex, endIndex),
    [items, startIndex, endIndex]
  );

  const columns = [
    {
      title: "Nom",
      dataIndex: "nom",
      key: "nom",
    },
    {
      title: "Obligatoire",
      dataIndex: "obligatoire",
      key: "obligatoire",
      render: (val) =>
        val ? <Tag color="green">Oui</Tag> : <Tag color="default">Non</Tag>,
    },
    {
      title: "Pour sinistre ?",
      dataIndex: "pieceSinistre",
      key: "pieceSinistre",
      render: (val) =>
        val ? <Tag color="blue">Oui</Tag> : <Tag color="default">Non</Tag>,
    },
    {
      title: "Validité (années)",
      dataIndex: "validite",
      key: "validite",
      render: (v) => (v ? Number(v) : <span className="text-muted">—</span>),
    },
    {
      title: "Commentaire",
      dataIndex: "commentaire",
      key: "commentaire",
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <ActionDropdown
          actions={[
            {
              key: "edit",
              label: "Modifier",
              icon: <i className="bx bx-edit-alt me-1" />,
              onClick: () => handleShow(true, record),
            },
            {
              key: "delete",
              label: "Supprimer",
              icon: <i className="bx bx-trash me-1" />,
              danger: true,
              onClick: () => handleDelete(record.typePieceId),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="PARAMETRES_SINISTRES" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
                onClick={() => handleShow()}
              >
                Ajouter un type de pièce
              </Button>

              <div className="card">
                <h5 className="card-header">Paramétrage — Types de pièces</h5>
                <div className="table-responsive text-nowrap">
                  {loadingTable ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: 200 }}
                    >
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                    </div>
                  ) : paginatedData.length > 0 ? (
                    <Table
                      columns={columns}
                      dataSource={paginatedData}
                      rowKey="typePieceId"
                      pagination={{ showSizeChanger: true }}
                    />
                  ) : (
                    <p className="text-center p-3">Aucun type de pièce trouvé.</p>
                  )}

                  {items.length > pageSize && (
                    <div className="d-flex justify-content-end me-3 mb-3 mt-3">
                      <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={items.length}
                        onChange={(page, size) => {
                          setCurrentPage(page);
                          setPageSize(size);
                        }}
                        showSizeChanger
                        pageSizeOptions={["5", "10", "20", "50"]}
                        showTotal={(total, range) =>
                          `${range[0]}-${range[1]} sur ${total} éléments`
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <AddTypesPieces
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedTypePiece={selectedTypePiece}
        onAddSuccess={handleAddSuccess}
        onUpdateSuccess={handleUpdateSuccess}
        existingTypePieces={items}
      />
    </div>
  );
};

export default TypePieces;
